import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import './SetupPage.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const SetupPage = ({ provider }) => {
  const history = useHistory();
  const [apiKey, setApiKey] = useState('');
  const [adminApiKey, setAdminApiKey] = useState(''); // OpenAI Admin Key
  const [organizationId, setOrganizationId] = useState('');
  const [gcpProjectId, setGcpProjectId] = useState('');
  const [gcpDatasetId, setGcpDatasetId] = useState('');
  const [gcpTableId, setGcpTableId] = useState('');
  const [serviceAccountEmail, setServiceAccountEmail] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [showAdminKey, setShowAdminKey] = useState(false); // Toggle for admin key
  const [testing, setTesting] = useState(false);
  const [testingAdmin, setTestingAdmin] = useState(false); // Testing admin key
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [adminTestResult, setAdminTestResult] = useState(null); // Admin key test result
  const [error, setError] = useState(null);

  const providerConfig = {
    claude: {
      name: 'Claude',
      icon: '🤖',
      color: '#D97757',
      fields: {
        apiKey: { label: 'Anthropic Admin API Key', placeholder: 'sk-ant-admin-...' },
        organizationId: { label: 'Organization ID (Optional)', placeholder: 'org_...' }
      },
      docs: 'https://console.anthropic.com/settings/keys'
    },
    gemini: {
      name: 'Gemini on GCP (WIF)',
      icon: '💎',
      color: '#4285F4',
      fields: {
        gcpProjectId: { label: 'GCP Project ID', placeholder: 'my-gcp-project-12345' },
        gcpDatasetId: { label: 'BigQuery Dataset ID', placeholder: 'billing_data' },
        gcpTableId: { label: 'BigQuery Table ID', placeholder: 'gcp_billing_export_v1_...' },
        serviceAccountEmail: { label: 'Impersonated Service Account Email', placeholder: 'my-sa@my-gcp-project.iam.gserviceaccount.com' }
      },
      docs: 'https://cloud.google.com/iam/docs/workload-identity-federation-with-other-providers',
    },
    openai: {
      name: 'OpenAI',
      icon: '🔮',
      color: '#10A37F',
      fields: {
        apiKey: { label: 'User API Key (for API calls)', placeholder: 'sk-proj-...' },
        adminApiKey: { label: 'Admin API Key (for usage data)', placeholder: 'sk-admin-...' },
        organizationId: { label: 'Organization ID (Optional)', placeholder: 'org-...' }
      },
      docs: 'https://platform.openai.com/api-keys',
      requiresBothKeys: true
    },
    grok: {
      name: 'Grok',
      icon: '⚡',
      color: '#000000',
      fields: {
        apiKey: { label: 'xAI API Key', placeholder: 'xai-...' },
        organizationId: { label: 'Organization ID (Optional)', placeholder: '' }
      },
      docs: 'https://x.ai/api'
    }
  };

  const config = providerConfig[provider];

  const handleTestConnection = async () => {
    if (provider === 'gemini') {
      setTestResult({ success: true, message: 'GCP connection can only be tested upon saving.' });
      return;
    }

    if (!apiKey) {
      setError('Please enter an API key');
      return;
    }

    setTesting(true);
    setError(null);
    setTestResult(null);

    try {
      const response = await axios.post(`${API_BASE_URL}/api/keys/${provider}/test`, {
        api_key: apiKey,
        organization_id: organizationId || null
      });

      setTestResult({
        success: true,
        message: response.data.message || 'User key validated successfully!'
      });
    } catch (err) {
      setTestResult({
        success: false,
        message: err.response?.data?.detail || 'Connection failed. Please check your API key.'
      });
    } finally {
      setTesting(false);
    }
  };

  const handleTestAdminKey = async () => {
    if (!adminApiKey) {
      setError('Please enter an admin API key');
      return;
    }

    setTestingAdmin(true);
    setError(null);
    setAdminTestResult(null);

    try {
      const response = await axios.post(`${API_BASE_URL}/api/keys/openai/test`, {
        api_key: adminApiKey
      });

      setAdminTestResult({
        success: true,
        message: 'Admin key validated successfully!'
      });
    } catch (err) {
      setAdminTestResult({
        success: false,
        message: err.response?.data?.detail || 'Admin key validation failed.'
      });
    } finally {
      setTestingAdmin(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      if (provider === 'gemini') {
        if (!gcpProjectId || !gcpDatasetId || !gcpTableId || !serviceAccountEmail) {
          setError('Please fill out all fields.');
          setSaving(false);
          return;
        }
        await axios.post(`${API_BASE_URL}/api/keys/gcp/configure`, {
          gcp_project_id: gcpProjectId,
          gcp_dataset_id: gcpDatasetId,
          gcp_table_id: gcpTableId,
          service_account_email: serviceAccountEmail
        });
      } else if (provider === 'openai') {
        // OpenAI requires both user and admin keys
        if (!apiKey || !adminApiKey) {
          setError('Please enter both User and Admin API keys for OpenAI');
          setSaving(false);
          return;
        }
        // Allow saving even if tests fail - we'll validate during sync instead

        // Save user key first
        await axios.post(`${API_BASE_URL}/api/keys/openai`, {
          api_key: apiKey,
          organization_id: organizationId || null
        });

        // Then save admin key
        await axios.post(`${API_BASE_URL}/api/keys/openai/admin`, {
          admin_api_key: adminApiKey
        });
      } else {
        // Other providers
        if (!apiKey) {
          setError('Please enter an API key');
          setSaving(false);
          return;
        }
        if (!testResult?.success) {
          setError('Please test the connection before saving');
          setSaving(false);
          return;
        }
        await axios.post(`${API_BASE_URL}/api/keys/${provider}`, {
          api_key: apiKey,
          organization_id: organizationId || null
        });
      }

      // Success - redirect to main app
      history.push('/main_app');
    } catch (err) {
      setError(err.response?.data?.detail || `Failed to save ${config.name} configuration`);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    history.push('/main_app');
  };

  const renderFormFields = () => {
    if (provider === 'gemini') {
      return (
        <>
          <div className="form-group">
            <label htmlFor="gcpProjectId">{config.fields.gcpProjectId.label}</label>
            <input
              type="text"
              id="gcpProjectId"
              value={gcpProjectId}
              onChange={(e) => setGcpProjectId(e.target.value)}
              placeholder={config.fields.gcpProjectId.placeholder}
              className="form-input"
            />
          </div>
          <div className="form-group">
            <label htmlFor="gcpDatasetId">{config.fields.gcpDatasetId.label}</label>
            <input
              type="text"
              id="gcpDatasetId"
              value={gcpDatasetId}
              onChange={(e) => setGcpDatasetId(e.target.value)}
              placeholder={config.fields.gcpDatasetId.placeholder}
              className="form-input"
            />
          </div>
          <div className="form-group">
            <label htmlFor="gcpTableId">{config.fields.gcpTableId.label}</label>
            <input
              type="text"
              id="gcpTableId"
              value={gcpTableId}
              onChange={(e) => setGcpTableId(e.target.value)}
              placeholder={config.fields.gcpTableId.placeholder}
              className="form-input"
            />
          </div>
          <div className="form-group">
            <label htmlFor="serviceAccountEmail">{config.fields.serviceAccountEmail.label}</label>
            <input
              type="email"
              id="serviceAccountEmail"
              value={serviceAccountEmail}
              onChange={(e) => setServiceAccountEmail(e.target.value)}
              placeholder={config.fields.serviceAccountEmail.placeholder}
              className="form-input"
            />
             <a href={config.docs} target="_blank" rel="noopener noreferrer" className="docs-link">
              How to set up WIF →
            </a>
          </div>
        </>
      );
    }

    // Default fields for other providers
    return (
      <>
        <div className="form-group">
          <label htmlFor="apiKey">{config.fields.apiKey.label}</label>
          <div className="input-with-toggle">
            <input
              type={showKey ? 'text' : 'password'}
              id="apiKey"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={config.fields.apiKey.placeholder}
              className="form-input"
            />
            <button
              type="button"
              onClick={() => setShowKey(!showKey)}
              className="toggle-visibility-btn"
            >
              {showKey ? '🙈' : '👁️'}
            </button>
          </div>
          <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
            <button
              type="button"
              onClick={handleTestConnection}
              disabled={testing || !apiKey}
              className="test-btn"
            >
              {testing ? 'Testing...' : 'Test Connection'}
            </button>
            {testResult && (
              <span className={`test-result ${testResult.success ? 'success' : 'error'}`}>
                {testResult.success ? '✓' : '✗'} {testResult.message}
              </span>
            )}
          </div>
          <a href={config.docs} target="_blank" rel="noopener noreferrer" className="docs-link">
            Get your API key →
          </a>
        </div>

        {provider === 'openai' && config.fields.adminApiKey && (
          <div className="form-group">
            <label htmlFor="adminApiKey">{config.fields.adminApiKey.label}</label>
            <div className="input-with-toggle">
              <input
                type={showAdminKey ? 'text' : 'password'}
                id="adminApiKey"
                value={adminApiKey}
                onChange={(e) => setAdminApiKey(e.target.value)}
                placeholder={config.fields.adminApiKey.placeholder}
                className="form-input"
              />
              <button
                type="button"
                onClick={() => setShowAdminKey(!showAdminKey)}
                className="toggle-visibility-btn"
              >
                {showAdminKey ? '🙈' : '👁️'}
              </button>
            </div>
            <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
              <button
                type="button"
                onClick={handleTestAdminKey}
                disabled={testingAdmin || !adminApiKey}
                className="test-btn"
              >
                {testingAdmin ? 'Testing...' : 'Test Admin Key'}
              </button>
              {adminTestResult && (
                <span className={`test-result ${adminTestResult.success ? 'success' : 'error'}`}>
                  {adminTestResult.success ? '✓' : '✗'} {adminTestResult.message}
                </span>
              )}
            </div>
            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
              ℹ️ Admin key is needed to fetch usage data from OpenAI's Usage API
            </div>
          </div>
        )}

        {config.fields.organizationId && (
          <div className="form-group">
            <label htmlFor="organizationId">{config.fields.organizationId.label}</label>
            <input
              type="text"
              id="organizationId"
              value={organizationId}
              onChange={(e) => setOrganizationId(e.target.value)}
              placeholder={config.fields.organizationId.placeholder}
              className="form-input"
            />
          </div>
        )}
      </>
    );
  };

  if (!config) {
    return <div>Invalid provider</div>;
  }

  return (
    <div className="setup-page">
      <div className="setup-container">
        <div className="setup-header">
          <div className="provider-icon-large" style={{ backgroundColor: `${config.color}20`, color: config.color }}>
            {config.icon}
          </div>
          <h1>Setup {config.name}</h1>
          <p className="setup-subtitle">
            Configure your {config.name} API credentials to start tracking usage and costs
          </p>
        </div>

        <div className="setup-form">
          {renderFormFields()}

          {error && (
            <div className="error-message">
              ❌ {error}
            </div>
          )}

          <div className="form-actions">
            <button
              onClick={handleSave}
              disabled={
                saving ||
                (provider === 'openai' && (!apiKey || !adminApiKey)) ||
                (provider !== 'gemini' && provider !== 'openai' && !testResult?.success) ||
                (provider === 'gemini' && (!gcpProjectId || !gcpDatasetId || !gcpTableId || !serviceAccountEmail))
              }
              className="save-btn"
            >
              {saving ? 'Saving...' : 'Save & Continue'}
            </button>

            <button
              onClick={handleCancel}
              className="cancel-btn"
            >
              Cancel
            </button>
          </div>
        </div>

        <div className="setup-info">
          <h3>🔒 Security</h3>
          <ul>
            <li>Your API key is encrypted and stored securely in Google Secret Manager</li>
            <li>Keys are never exposed in logs or error messages</li>
            <li>ISO 27001 and SOC 2 compliant infrastructure</li>
            <li>You can update or delete your keys at any time</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SetupPage;
